import React, { useState } from 'react';
import { useMarkerValuations } from '../../../services/markerValuationService';

const MARKETPLACE_BASE = 'https://www.kromarketplace.com';

const markerBreakdown = [
  { denom: 'kro.gipr.genesis.v2', label: 'Genesis Bond', symbol: 'KRO-GIPR', units: 60, price: 1000000, total: 60000000, color: '#f59e0b', esgImpact: 'Foundation capital for trust-based infrastructure. Anchors the KRO Trust Index and provides the economic base layer for institutional stewardship.', zoneScan: 'https://zonescan.io/provenance/assets/kro.gipr.genesis.v2', marketplaceProduct: 'Genesis Anchor' },
  { denom: 'kro.as.accountability.v2', label: 'Accountability', symbol: 'KRO-AS', units: 182, price: 5000000, total: 910000000, color: '#818cf8', esgImpact: 'Accountability markers enforce transparent governance and verifiable stewardship. Each unit represents a programmable claim on institutional-grade collateral.', zoneScan: 'https://zonescan.io/provenance/assets/kro.as.accountability.v2', marketplaceProduct: 'Accountability Marker' },
  { denom: 'kro.sc.stewardship.v2', label: 'Stewardship Draw', symbol: 'KRO-SC', units: 12, price: 20000000, total: 240000000, color: '#10b981', esgImpact: 'Stewardship draws fund social purpose allocations under SPC mandate. Each draw activates a discretionary social impact tranche tied to sovereign treasury draws.', zoneScan: 'https://zonescan.io/provenance/assets/kro.sc.stewardship.v2', marketplaceProduct: 'Stewardship Draw' },
];

const esgPillars = [
  { pillar: 'Governance Integrity', score: 97, icon: 'bi-shield-check', color: '#10b981', kpis: ['Multi-party Daml authorization — no single-party execution', 'kroOS observer-only role on all settlement contracts', 'Canton sub-transaction privacy preserves institutional sovereignty', 'Legal observer parties on encumbrance and closeout events'] },
  { pillar: 'Collateral Transparency', score: 94, icon: 'bi-eye-fill', color: '#38bdf8', kpis: ['All 254 on-chain units verifiable on ZoneScan (Provenance block explorer)', 'DTIF registry IDs per ISO 24165 — globally identifiable tokens', 'Real-time oracle NAV revaluation with 15-min margin monitoring cycle', 'CIP-0056 allocation locking — net amounts reserved, capital drag minimized'] },
  { pillar: 'Settlement Soundness', score: 96, icon: 'bi-lightning-charge-fill', color: '#f59e0b', kpis: ['Deterministic T+0 atomic DvP — both legs settle or both roll back', 'Canton Network open-source DvP standards', '10-step settlement pipeline with 4 binary compliance gates', '24/7 settlement-ready — no T+1 batch dependency'] },
  { pillar: 'Social Purpose', score: 88, icon: 'bi-heart-fill', color: '#f472b6', kpis: ['SPC corporate mandate for discretionary social purpose allocation', 'Stewardship Draw tranche (~7.82% target) tied to settlement value', 'ESG audit receipt generated for each social purpose distribution'] },
];

const marketplaceProducts = [
  { name: 'Genesis Anchor Bond', category: 'Fixed Income', aum: '$60M', yield: '4.85%', status: 'Active', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-gem', color: '#f59e0b' },
  { name: 'Accountability Marker Series', category: 'Structured Product', aum: '$910M', yield: 'Variable', status: 'Active', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-shield-fill-check', color: '#818cf8' },
  { name: 'Stewardship Draw Facility', category: 'Credit Facility', aum: '$240M', yield: 'Repo Rate', status: 'Active', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-tree-fill', color: '#10b981' },
  { name: 'KRO Trust Index', category: 'Index / Scoring', aum: '—', yield: '—', status: 'Live', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-award-fill', color: '#c9a84c' },
  { name: 'Intraday Repo Settlement', category: 'Settlement Service', aum: '$37.3B capacity', yield: '—', status: 'Sandbox', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-lightning-charge-fill', color: '#38bdf8' },
  { name: 'Document Key Vault', category: 'Encryption Service', aum: '—', yield: '—', status: 'Active', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-key-fill', color: '#a78bfa' },
];

const FoundationalInvestment = () => {
  const [activeView, setActiveView] = useState('overview');
  const { markers: liveMarkers, isLive, refreshedAt } = useMarkerValuations(60_000);

  // Overlay live on-chain supply/value (from the Provenance oracle) onto the
  // static marker metadata; falls back to the baked-in values if the API is down.
  const liveBreakdown = markerBreakdown.map((m) => {
    const live = liveMarkers[m.denom];
    return live
      ? { ...m, units: live.units, price: live.price_per_unit_usd, total: live.total_value_usd }
      : m;
  });
  const totalAUM = liveBreakdown.reduce((sum, m) => sum + m.total, 0);
  const totalUnits = liveBreakdown.reduce((sum, m) => sum + m.units, 0);

  const summaryMetrics = [
    { label: 'Assets Under Stewardship', value: `$${(totalAUM / 1e9).toFixed(2)}B`, icon: 'bi-safe2', color: '#c9a84c', desc: 'Total marked value across 3 on-chain marker denoms on Provenance (pio-mainnet-1)' },
    { label: 'Settlement Capacity', value: '$37.3B+', icon: 'bi-lightning-charge-fill', color: '#38bdf8', desc: 'Full commercial settlement capacity of the private-wealth foundation — revolving intraday repo facility, regenerates each cycle' },
    { label: 'Marker Denoms Active', value: String(liveBreakdown.length), icon: 'bi-tag-fill', color: '#10b981', desc: 'Genesis Bond (KRO-GIPR) · Accountability (KRO-AS) · Stewardship Draw (KRO-SC)' },
    { label: 'On-Chain Units', value: String(totalUnits), icon: 'bi-box-seam-fill', color: '#a78bfa', desc: `${liveBreakdown.map((m) => m.units).join(' + ')} units — ZoneScan verifiable` },
  ];

  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(); } catch { return '—'; } };

  return (
    <div className="container-fluid py-4" style={{ color: '#e2e8f0' }}>
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(201,168,76,0.15)' }}>
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#c9a84c' }}>
                <i className="bi bi-bank2 me-2"></i>Foundational Investment & ESG Impact
              </h2>
              <p className="mb-0" style={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                ${(totalAUM / 1e9).toFixed(2)}B+ assets under stewardship · $37.3B+ settlement capacity · Integrated with KRO Marketplace products
              </p>
              <small className="d-inline-flex align-items-center gap-1 mt-1" style={{ fontSize: '0.7rem', color: isLive ? '#10b981' : '#f59e0b' }}>
                <span className="rounded-circle" style={{ width: 7, height: 7, background: isLive ? '#10b981' : '#f59e0b', display: 'inline-block' }}></span>
                {isLive ? `Live — Provenance oracle${refreshedAt ? ` · updated ${fmtTime(refreshedAt)}` : ''}` : 'Showing last verified values (oracle offline)'}
              </small>
            </div>
            <div className="d-flex gap-2">
              <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ background: '#c9a84c', color: '#0f172a', fontWeight: 700 }}>
                <i className="bi bi-shop me-1"></i>Browse Products
              </a>
              <a href={`${MARKETPLACE_BASE}/ttd-portal?page=portfolio`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light">
                <i className="bi bi-window-fullscreen me-1"></i>TTD Portal
              </a>
            </div>
          </div>

          <ul className="nav nav-pills mb-4">
            {[
              { id: 'overview', label: 'Overview', icon: 'bi-grid-fill' },
              { id: 'markers', label: 'Marker Breakdown', icon: 'bi-tag-fill' },
              { id: 'esg', label: 'ESG Impact', icon: 'bi-heart-pulse-fill' },
              { id: 'products', label: 'Marketplace Products', icon: 'bi-shop' },
            ].map(v => (
              <li key={v.id} className="nav-item">
                <button className={`nav-link ${activeView === v.id ? 'active' : ''}`} onClick={() => setActiveView(v.id)}>
                  <i className={`bi ${v.icon} me-1`}></i>{v.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ════ OVERVIEW ════ */}
      {activeView === 'overview' && (
        <>
          <div className="row g-3 mb-4">
            {summaryMetrics.map((m, i) => (
              <div key={i} className="col-md-6 col-xl-3">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="card-body text-center">
                    <i className={`bi ${m.icon} d-block mb-2`} style={{ fontSize: '2rem', color: m.color }}></i>
                    <h2 className="fw-bold mb-1" style={{ color: m.color }}>{m.value}</h2>
                    <h6 className="mb-1" style={{ color: '#e2e8f0' }}>{m.label}</h6>
                    <small style={{ color: '#64748b', fontSize: '0.72rem' }}>{m.desc}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm" style={{ background: '#0f172a' }}>
                <div className="card-header border-0" style={{ background: 'linear-gradient(135deg, #1e1b4b, #0f172a)' }}>
                  <h5 className="mb-0 fw-bold text-white"><i className="bi bi-pie-chart-fill me-2 text-warning"></i>Asset Composition</h5>
                </div>
                <div className="card-body">
                  {liveBreakdown.map((m, i) => {
                    const pct = ((m.total / totalAUM) * 100).toFixed(1);
                    return (
                      <div key={i} className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="rounded-circle" style={{ width: 10, height: 10, background: m.color, display: 'inline-block' }}></span>
                            <strong style={{ fontSize: '0.88rem' }}>{m.label}</strong>
                            <span className="badge" style={{ background: `${m.color}22`, color: m.color, fontSize: '0.65rem' }}>{m.symbol}</span>
                          </div>
                          <span className="fw-bold" style={{ color: m.color }}>${(m.total / 1e6).toFixed(0)}M ({pct}%)</span>
                        </div>
                        <div className="progress" style={{ height: 8, background: 'rgba(255,255,255,0.05)' }}>
                          <div className="progress-bar" style={{ width: `${pct}%`, background: m.color }}></div>
                        </div>
                        <small style={{ color: '#64748b', fontSize: '0.72rem' }}>{m.units} units × ${(m.price / 1e6).toFixed(0)}M/unit</small>
                      </div>
                    );
                  })}
                  <div className="mt-3 p-2 rounded" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                    <div className="d-flex justify-content-between">
                      <strong style={{ color: '#c9a84c' }}>Total Assets Under Stewardship</strong>
                      <strong style={{ color: '#c9a84c', fontSize: '1.1rem' }}>${(totalAUM / 1e9).toFixed(2)}B</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a' }}>
                <div className="card-header border-0" style={{ background: 'linear-gradient(135deg, #064e3b, #0f172a)' }}>
                  <h5 className="mb-0 fw-bold text-white"><i className="bi bi-graph-up-arrow me-2 text-success"></i>ESG Impact Score</h5>
                </div>
                <div className="card-body">
                  {esgPillars.map((p, i) => (
                    <div key={i} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.82rem' }}><i className={`bi ${p.icon} me-1`} style={{ color: p.color }}></i>{p.pillar}</span>
                        <span className="badge" style={{ background: `${p.color}22`, color: p.color }}>{p.score}/100</span>
                      </div>
                      <div className="progress" style={{ height: 6, background: 'rgba(255,255,255,0.05)' }}>
                        <div className="progress-bar" style={{ width: `${p.score}%`, background: p.color }}></div>
                      </div>
                    </div>
                  ))}
                  <div className="text-center mt-3">
                    <div className="display-5 fw-bold" style={{ color: '#10b981' }}>
                      {(esgPillars.reduce((s, p) => s + p.score, 0) / esgPillars.length).toFixed(1)}
                    </div>
                    <small style={{ color: '#94a3b8' }}>Composite ESG Score</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ════ MARKER BREAKDOWN ════ */}
      {activeView === 'markers' && (
        <div className="row g-3">
          {liveBreakdown.map((m, i) => (
            <div key={i} className="col-lg-4">
              <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${m.color}22` }}>
                <div className="card-header border-0" style={{ background: `${m.color}08` }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="mb-0 fw-bold" style={{ color: m.color }}>{m.label}</h5>
                      <small style={{ color: '#94a3b8' }}>{m.symbol} · {m.denom}</small>
                    </div>
                    <span className="badge bg-success">ACTIVE</span>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <small style={{ color: '#64748b' }}>Units</small>
                      <div className="fw-bold fs-5" style={{ color: '#e2e8f0' }}>{m.units}</div>
                    </div>
                    <div className="col-6">
                      <small style={{ color: '#64748b' }}>Price/Unit</small>
                      <div className="fw-bold fs-5" style={{ color: '#e2e8f0' }}>${(m.price / 1e6).toFixed(0)}M</div>
                    </div>
                    <div className="col-12">
                      <small style={{ color: '#64748b' }}>Total Marked Value</small>
                      <div className="fw-bold fs-4" style={{ color: m.color }}>${(m.total / 1e6).toFixed(0)}M</div>
                    </div>
                  </div>
                  <div className="p-2 rounded mb-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <small style={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '1px' }}>ESG Impact</small>
                    <p className="mb-0" style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.5 }}>{m.esgImpact}</p>
                  </div>
                  <div className="d-flex gap-2">
                    <a href={m.zoneScan} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary flex-fill" style={{ fontSize: '0.72rem' }}>
                      <i className="bi bi-search me-1"></i>Verify on ZoneScan
                    </a>
                    <a href={`${MARKETPLACE_BASE}/ttd-portal?page=portfolio`} target="_blank" rel="noopener noreferrer" className="btn btn-sm flex-fill" style={{ fontSize: '0.72rem', background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}44` }}>
                      <i className="bi bi-shop me-1"></i>View on Exchange
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ════ ESG IMPACT ════ */}
      {activeView === 'esg' && (
        <div className="row g-3">
          {esgPillars.map((p, i) => (
            <div key={i} className="col-lg-6">
              <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a' }}>
                <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: `${p.color}08` }}>
                  <h5 className="mb-0 fw-bold" style={{ color: p.color }}>
                    <i className={`bi ${p.icon} me-2`}></i>{p.pillar}
                  </h5>
                  <div className="text-center">
                    <span className="display-6 fw-bold" style={{ color: p.color }}>{p.score}</span>
                    <small className="d-block" style={{ color: '#64748b', fontSize: '0.65rem' }}>/ 100</small>
                  </div>
                </div>
                <div className="card-body">
                  <div className="progress mb-3" style={{ height: 8, background: 'rgba(255,255,255,0.05)' }}>
                    <div className="progress-bar" style={{ width: `${p.score}%`, background: p.color }}></div>
                  </div>
                  {p.kpis.map((kpi, ki) => (
                    <div key={ki} className="d-flex align-items-start gap-2 mb-2">
                      <i className="bi bi-check-circle-fill text-success mt-1 flex-shrink-0" style={{ fontSize: '0.75rem' }}></i>
                      <small style={{ color: '#94a3b8', fontSize: '0.82rem' }}>{kpi}</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ════ MARKETPLACE PRODUCTS ════ */}
      {activeView === 'products' && (
        <>
          <div className="alert border-0 mb-4" style={{ background: 'linear-gradient(135deg, #1e1b4b, #0f172a)', color: '#e2e8f0' }}>
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-shop text-warning fs-5"></i>
              <div>
                <strong>KRO Marketplace — Integrated Products & Services</strong>
                <small className="d-block" style={{ color: '#94a3b8' }}>
                  Products listed on <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c' }}>kromarketplace.com/browse-products</a>.
                  KRO is settlement infrastructure and admin governance — compliant by design. Not a bank, broker, or clearing agent.
                </small>
              </div>
            </div>
          </div>
          <div className="row g-3">
            {marketplaceProducts.map((p, i) => (
              <div key={i} className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${p.color}22` }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <i className={`bi ${p.icon}`} style={{ color: p.color, fontSize: '1.5rem' }}></i>
                      <div className="flex-grow-1">
                        <strong style={{ fontSize: '0.92rem' }}>{p.name}</strong>
                        <small className="d-block" style={{ color: '#64748b' }}>{p.category}</small>
                      </div>
                      <span className={`badge bg-${p.status === 'Active' || p.status === 'Live' ? 'success' : 'warning text-dark'}`} style={{ fontSize: '0.6rem' }}>{p.status}</span>
                    </div>
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <small style={{ color: '#64748b' }}>AUM / Capacity</small>
                        <div className="fw-bold" style={{ color: '#e2e8f0' }}>{p.aum}</div>
                      </div>
                      <div className="col-6">
                        <small style={{ color: '#64748b' }}>Yield</small>
                        <div className="fw-bold" style={{ color: '#e2e8f0' }}>{p.yield}</div>
                      </div>
                    </div>
                    <a href={p.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm w-100" style={{ background: `${p.color}15`, color: p.color, border: `1px solid ${p.color}33`, fontSize: '0.78rem' }}>
                      <i className="bi bi-box-arrow-up-right me-1"></i>View on KRO Exchange
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Disclaimer */}
      <div className="mt-4 p-3 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', color: '#475569' }}>
        <i className="bi bi-info-circle me-1"></i>
        KRO Brokers, SPC operates as settlement infrastructure and admin governance — compliant by design.
        KRO is not a bank, SEC-regulated broker, clearing agent, or counterparty.
        All marker data is on-chain and verifiable on ZoneScan (Provenance block explorer).
        Settlement capacity reflects the revolving intraday repo facility modeled on Canton Foundation open-source standards.
        Product listings reference the KRO Marketplace (<a href={MARKETPLACE_BASE} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>kromarketplace.com</a>).
      </div>
    </div>
  );
};

export default FoundationalInvestment;
